import React, { useEffect, useState } from 'react';
import {
  Box,
  HStack,
  Text,
  VStack,
  Checkbox,
  DrawerRoot,
  DrawerBackdrop,
  DrawerPositioner,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerCloseTrigger,
} from '@chakra-ui/react';
import { ICatalogResponse } from '../../../models/sessions';
import CustomButton from '../../../shared/CustomButton/CustomButton';

interface ColumnsDrawerProps {
  open: boolean;
  onClose: () => void;
  catalog: ICatalogResponse | undefined;
  activeColumns: string[];
  onApply: (columns: string[]) => void;
}

const GROUP_LABELS: Record<string, string> = {
  identification: 'Identificativo sessione',
  internal: 'Carico interno',
  metabolic: 'Metabolic',
  volume: 'Volume e zone distanza',
  events: 'Accelerazioni ed eventi',
  advanced: 'Avanzate',
  running: 'Corsa',
  cycling: 'Ciclismo',
  endurance_common: 'Comuni endurance',
};

/**
 * Drawer Configura Colonne (Addendum v1 Parte 4).
 * MVP: checkbox raggruppate per categoria semantica + Applica.
 * NON-MVP: salva preset custom, badge provider non-supportato, hint suggeriti.
 */
export const ColumnsDrawer: React.FC<ColumnsDrawerProps> = ({
  open,
  onClose,
  catalog,
  activeColumns,
  onApply,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) setSelected(new Set(activeColumns));
  }, [open, activeColumns]);

  const grouped: Record<string, ICatalogResponse['metrics']> = {};
  if (catalog) {
    for (const m of catalog.metrics) {
      if (!grouped[m.group]) grouped[m.group] = [];
      grouped[m.group].push(m);
    }
  }

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <DrawerRoot
      open={open}
      onOpenChange={(e) => { if (!e.open) onClose(); }}
      placement="end"
      size="md"
    >
      <DrawerBackdrop />
      <DrawerPositioner>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              Configura colonne
              <Text as="span" fontSize="xs" color="gray.500" fontWeight={400} ml={2}>
                {selected.size} selezionate
              </Text>
            </DrawerTitle>
            <DrawerCloseTrigger />
          </DrawerHeader>

          <DrawerBody>
            {catalog ? (
              <VStack gap={4} alignItems="stretch">
                {Object.entries(grouped).map(([group, metrics]) => (
                  <Box key={group}>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color="gray.600"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                      mb={2}
                    >
                      {GROUP_LABELS[group] ?? group} (
                      {metrics.filter((m) => selected.has(m.id)).length}/
                      {metrics.length})
                    </Text>
                    <VStack gap={1} alignItems="stretch">
                      {metrics.map((m) => (
                        <HStack
                          key={m.id}
                          gap={2}
                          p={2}
                          borderRadius="md"
                          _hover={{ bg: 'gray.50' }}
                          cursor="pointer"
                          onClick={() => toggle(m.id)}
                        >
                          <Checkbox.Root
                            checked={selected.has(m.id)}
                            onCheckedChange={() => toggle(m.id)}
                            colorPalette="green"
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                          </Checkbox.Root>
                          <Text flex={1} fontSize="sm">
                            {m.label}
                          </Text>
                          <Text fontSize="xs" color="gray.400" fontFamily="mono">
                            {m.unit}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.400">
                Catalogo non disponibile.
              </Text>
            )}
          </DrawerBody>

          <DrawerFooter>
            <CustomButton color="text" onClick={onClose}>
              Annulla
            </CustomButton>
            <CustomButton
              color="primary"
              onClick={() => {
                onApply(Array.from(selected));
                onClose();
              }}
            >
              Applica
            </CustomButton>
          </DrawerFooter>
        </DrawerContent>
      </DrawerPositioner>
    </DrawerRoot>
  );
};
